import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  computeI3,
  computeRootCauseTriggers,
  I3SectionResult,
  I3Zone,
  RootCauseTriggerResult,
} from '../../models/ImmuneInflammatory';
import { MUTED_PALETTE } from '../charts/chartPalette';

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
}

const GAUGE_PALETTE = { ...MUTED_PALETTE, gray: '#e3e3e3' };
const BAND_PALETTE = { green: '#3fa34d', yellow: '#c79a00', orange: '#e3741a', red: '#d8472f', gray: '#b7b7b7' };
const TRACK_BG = '#e9ecf1';
const TRACK_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.06)';
const BAR_HEIGHT = 14;

// Zone → non-diagnostic "priority to investigate" wording + chip colors. Higher
// zones read as more worth investigating (observational, not a diagnosis).
const PRIORITY_META: Record<I3Zone, { label: string; fill: string; dark: string; fg: string }> = {
  green: { label: 'Low priority', fill: GAUGE_PALETTE.green, dark: BAND_PALETTE.green, fg: '#14521d' },
  yellow: { label: 'Moderate priority', fill: GAUGE_PALETTE.yellow, dark: BAND_PALETTE.yellow, fg: '#2e2a00' },
  orange: { label: 'Elevated priority', fill: GAUGE_PALETTE.orange, dark: BAND_PALETTE.orange, fg: '#3d2700' },
  red: { label: 'High priority', fill: GAUGE_PALETTE.red, dark: BAND_PALETTE.red, fg: '#fff' },
};

export class RootCauseFrameworkPanel extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis): boolean {
    return !!ba && !!ba.bloodwork && computeI3(ba.bloodwork).anyEntered;
  }

  // Colored priority chip, styled like the thyroid panel's likelihood chips.
  renderChip(zone: I3Zone) {
    const meta = PRIORITY_META[zone];
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '1px 8px',
          borderRadius: '10px',
          background: meta.fill,
          color: meta.fg,
          fontSize: '11px',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label}
      </span>
    );
  }

  renderTriggerBars(triggers: RootCauseTriggerResult[]) {
    const ordered = [...triggers].sort((a, b) => b.score - a.score);
    return (
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {ordered.map(t => {
            const score = Math.round(t.score);
            return (
              <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px' }}>
                <span style={{ width: '72px', flexShrink: 0, fontSize: '11.5px', fontWeight: 600, color: '#333' }}>
                  {t.label}
                </span>
                <div
                  style={{
                    position: 'relative',
                    flex: 1,
                    height: `${BAR_HEIGHT}px`,
                    borderRadius: '4px',
                    background: TRACK_BG,
                    boxShadow: TRACK_INSET,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${t.score}%`,
                      minWidth: t.score > 0 ? '6px' : '0',
                      background: GAUGE_PALETTE[t.zone],
                      borderRadius: '4px',
                    }}
                  />
                  {t.score > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        left: `${t.score}%`,
                        top: '-1px',
                        bottom: '-1px',
                        width: '2px',
                        borderRadius: '1px',
                        transform: 'translateX(-100%)',
                        background: BAND_PALETTE[t.zone],
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    width: '34px',
                    flexShrink: 0,
                    textAlign: 'right',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: BAND_PALETTE[t.zone],
                  }}
                >
                  {score}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // One box per trigger at or above 50% (Elevated / High priority), most-likely
  // first — lists the contributing sections (primary bold), and notes the toggle.
  renderTriggerBox(t: RootCauseTriggerResult, byNum: Map<number, I3SectionResult>) {
    const line = (nums: number[], bold: boolean) =>
      nums.map(n => {
        const s = byNum.get(n);
        if (!s) return null;
        return (
          <span key={n} style={{ display: 'inline-block', marginRight: '10px', color: '#333' }}>
            <span style={{ fontWeight: bold ? 700 : 500 }}>{s.label}</span>
            <span style={{ color: '#888' }}> {s.hasValue ? `${s.raw}/${s.max}` : '—'}</span>
          </span>
        );
      });
    return (
      <div className="condition-section" key={t.key}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ color: 'black', textAlign: 'left', fontWeight: 700, fontSize: '14px' }}>{t.label}</span>
          {this.renderChip(t.zone)}
        </div>
        <div style={{ textAlign: 'left', fontSize: '12px', marginTop: '4px' }}>
          <div style={{ fontWeight: 600, color: 'black', marginTop: '4px' }}>Primary sections</div>
          <div>{line(t.primary, true)}</div>
          {t.secondary.length > 0 && (
            <>
              <div style={{ fontWeight: 600, color: 'black', marginTop: '4px' }}>Contributing sections</div>
              <div>{line(t.secondary, false)}</div>
            </>
          )}
          {t.toggleApplied && (
            <div style={{ marginTop: '4px', color: '#666', fontStyle: 'italic' }}>
              Includes the practitioner flag for this trigger.
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis || !bloodworkAnalysis.bloodwork) return null;

    const i3 = computeI3(bloodworkAnalysis.bloodwork);
    if (!i3.anyEntered) return null;

    const triggers = computeRootCauseTriggers(bloodworkAnalysis.bloodwork);
    const byNum = new Map<number, I3SectionResult>(i3.sections.map(s => [s.num, s]));
    const boxes = [...triggers].sort((a, b) => b.score - a.score).filter(t => t.score >= 50);

    return (
      <Card className="evaluationPanel i3EvaluationPanel" id="root-cause-framework-insights">
        <Card.Header className="evaluationPanelHeading">Root Cause Framework</Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {this.renderTriggerBars(triggers)}
            {boxes.map(t => this.renderTriggerBox(t, byNum))}
            <div style={{ textAlign: 'left', fontSize: '11px', color: '#888', marginTop: '10px' }}>
              Scores are derived from the I³ questionnaire sections (primary
              sections weighted more heavily). Toxins and Trauma share the same
              sections and are separated by the two flags on Add Test Results.
              These are observational cues for where to investigate — not a
              diagnosis.
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default RootCauseFrameworkPanel;
